// src/features/analytics/utils/loyaltyModel.ts
// Detecta cómo USA cada negocio el programa de lealtad (aunque todos estén
// configurados como loyaltyMode='visits'). En la práctica hay 3 patrones reales:
//   - 'sellos'        : 1+ puntos por visita, canjes ≪ visitas (tarjeta de sellos clásica)
//   - 'puntos-canje'  : casi ninguna visita otorga puntos; los puntos se cargan aparte
//                       y se canjean. Las métricas de visita NO miden la lealtad aquí.
//   - 'nuevo'         : muy pocos datos; mostrar onboarding en vez de gráficas vacías.

export type LoyaltyModel = 'sellos' | 'puntos-canje' | 'nuevo';

export interface LoyaltyModelInput {
  totalVisits: number;
  totalPointsGiven: number;
  totalRedemptions?: number; // opcional; si viene, afina la detección
  loyaltyMode?: string;      // config real del negocio: 'visits' | 'cashback'
}

export interface LoyaltyModelInfo {
  model: LoyaltyModel;
  label: string;       // texto del badge para el dueño
  badgeClass: string;  // clase de color del badge (ver tailwind abajo)
  pointsPerVisit: number;
}

const MIN_VISITS = 30;          // por debajo de esto: negocio nuevo / sin datos
const POINTS_PER_VISIT_FLAT = 0.5; // por debajo: casi ninguna visita da puntos
const REDEEM_RATIO_HIGH = 0.8;     // canjes ≈ o > visitas: modelo de canje

export function detectLoyaltyModel(input: LoyaltyModelInput): LoyaltyModelInfo {
  const { totalVisits, totalPointsGiven, loyaltyMode } = input;
  const totalRedemptions = input.totalRedemptions ?? 0;
  const pointsPerVisit = totalVisits > 0 ? totalPointsGiven / totalVisits : 0;
  const redeemRatio = totalVisits > 0 ? totalRedemptions / totalVisits : 0;

  // Clasificación de PATRÓN de uso (sirve para adaptar qué métricas mostrar)
  let model: LoyaltyModel;
  if (totalVisits < MIN_VISITS) {
    model = 'nuevo';
  } else if (pointsPerVisit < POINTS_PER_VISIT_FLAT || redeemRatio >= REDEEM_RATIO_HIGH) {
    model = 'puntos-canje';
  } else {
    model = 'sellos';
  }

  // Nombre del badge: refleja cómo el dueño piensa su programa, según su
  // configuración real (loyaltyMode) + el patrón detectado.
  const BLUE = 'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300';
  const GREEN = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300';
  const FUCHSIA = 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-500/20 dark:text-fuchsia-300';
  const YELLOW = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-300';

  let label: string, badgeClass: string;
  if (model === 'nuevo') {
    label = 'Negocio nuevo'; badgeClass = YELLOW;
  } else if (loyaltyMode === 'cashback') {
    label = 'Cashback'; badgeClass = GREEN;
  } else if (model === 'puntos-canje') {
    label = 'Por puntos'; badgeClass = FUCHSIA;
  } else {
    label = 'Por visitas'; badgeClass = BLUE;
  }

  return { model, label, badgeClass, pointsPerVisit };
}
